import { AddIcon } from "@chakra-ui/icons";
import {
  Box,
  Button,
  Flex,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalOverlay,
  useDisclosure,
  Input,
  Text,
  Textarea
} from "@chakra-ui/react";
import React, { useEffect, useState } from "react";
import { pxToRem } from "../../utils/editor.ts";
import useEditorStore from "../../stores/modules/editor.ts";
import { PaperService } from "../../services/paperService";
import LatexItemRow from "./LatexItemRow";
import { LatexListData } from "../../types/latexPackage.ts";
import { ClientError } from "../../utils/clientError.ts";

const LatexPanel: React.FC<{
  paperId: string;
}> = ({ paperId }) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [latexPackages, setLatexPackages] = useState<LatexListData[]>([]);
  const [newPackage, setNewPackage] = useState<string>("");
  const { setFocusEditor } = useEditorStore();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [editingPackage, setEditingPackage] = useState<LatexListData | null>(
    null
  );

  // 加载已有的Latex包
  useEffect(() => {
    const fetchLatexPackages = async () => {
      try {
        const packages = await PaperService.getLatexPackages(paperId);
     
        setLatexPackages(packages);
      } catch (error) {
        new ClientError(error).toast();
      }
    };

    fetchLatexPackages();
  }, [paperId]);

  const addLatexPackage = () => {
    setIsEditing(false);
    setEditingPackage(null);
    setNewPackage("");
    setFocusEditor(false);
    onOpen();
  };

  // 编辑Latex包
  const editLatexPackage = (resource: LatexListData) => {
    setIsEditing(true);
    setEditingPackage(resource);
    setNewPackage(resource.latexPackage);
    setFocusEditor(false);
    onOpen();
  };

  // 清除是否要将内容里面的latex都清除？
  const removePackage = async (index: number) => {
    const removelatexPackage = latexPackages[index];
    try {
      await PaperService.deleteLatexList({
        latexPackageId: removelatexPackage?.id,
        paperId,
      });

      setLatexPackages((prevPackages) => {
        return prevPackages.filter((_, i) => i !== index);
      });
    } catch (error) {
      new ClientError(error).toast();
    }
  };

  const handleConfirm = async () => {
    if (!newPackage.trim()) return;

    setIsLoading(true);
    try {
      if (isEditing && editingPackage) {
        // 编辑现有包
        const res = await PaperService.upadteLatexList({
          paperId,
          latexPackage: newPackage,
          latexPackageId: editingPackage.id,
        });

        setLatexPackages((prev) =>
          prev.map((pkg) => (pkg.id === editingPackage.id ? res : pkg))
        );
      } else {
        // 添加新包
        const res = await PaperService.addLatexPackage({
          paperId,
          latexPackage: newPackage,
        });
        setLatexPackages((prev) => [...prev, ...( res || [])]);
      }

      closeModal();
    } catch (error) {
      new ClientError(error).toast();
    } finally {
      setIsLoading(false);
    }
  };

  const closeModal = () => {
    setNewPackage("");
    setIsEditing(false);
    setEditingPackage(null);
    onClose();
    setFocusEditor(true);
  };

  return (
    <Flex
      flexDir={"column"}
      onClick={(e) => e.stopPropagation()}
      overflow={"hidden"}
    >
      <Flex
        px={"0.5rem"}
        flexDir={"column"}
        overflow={"auto"}
        h={pxToRem(120)}
        css={{
          "&::-webkit-scrollbar": {
            width: "4px",
          },
          "&::-webkit-scrollbar-thumb": {
            background: "#9EC3E8",
            borderRadius: "2px",
          },
          "&::-webkit-scrollbar-thumb:hover": {
            background: "#2b6cb0",
          },
        }}
      >
        {latexPackages.map((pkg, index) => (
          <LatexItemRow
            key={pkg.id}
            resource={pkg}
            packageOnDelete={() => removePackage(index)}
            packageOnEdit={editLatexPackage}
          />
        ))}
      </Flex>
      <Flex flexDir={"row"} justifyContent={"space-between"} pt={5} pb={2}>
        <Box>
          {/* <Button w={pxToRem(99)} h={pxToRem(33)}
                  size={"xs"}
                  variant={'outline'}
                  fontSize={pxToRem(14)}
                  onClick={insertLatexPackage}
          >
            Apply
          </Button> */}
        </Box>
        <Box>
          <Button
            w={pxToRem(99)}
            h={pxToRem(33)}
            size={"sm"}
            variant={"solid"}
            colorScheme="brand"
            fontSize={pxToRem(14)}
            leftIcon={<AddIcon />}
            onClick={() => addLatexPackage()}
          >
            Add
          </Button>
        </Box>
      </Flex>

      <Modal
        isOpen={isOpen}
        onClose={closeModal}
        size={"sm"}
        closeOnOverlayClick={false}
        isCentered
      >
        <ModalOverlay bg={"rgba(0, 0, 0, 0.1)"} />
        <ModalContent
          maxW={pxToRem(566)}
          bg={"white"}
          borderRadius={pxToRem(24)}
          boxShadow="none"
          p={0}
        >
          <ModalCloseButton
            _focusVisible={{ boxShadow: "none" }}
            color={"#666"}
            top={pxToRem(12)}
            right={pxToRem(12)}
            fontSize={pxToRem(14)}
            borderRadius={"50%"}
          />
          <ModalBody p={pxToRem(30)}>
            <Text fontSize={pxToRem(18)} fontWeight="500" mb={pxToRem(20)}>
              {isEditing ? "edit LaTeX preamble" : "add LaTeX preamble (package, new command, new environment, etc.)"}
            </Text>
            {isEditing ? (
              <Input
                value={newPackage}
                onChange={(e) => setNewPackage(e.target.value)}
                mb={pxToRem(40)}
                mt={pxToRem(20)}
                autoFocus={true}
                borderRadius={pxToRem(6)}
                borderColor="#e2e8f0"
                fontSize={pxToRem(14)}
              />
            ) : (
              <Textarea
                value={newPackage}
                onChange={(e) => setNewPackage(e.target.value)}
                mb={pxToRem(40)}
                mt={pxToRem(20)}
                autoFocus={true}
                borderRadius={pxToRem(6)}
                borderColor="#e2e8f0"
                fontSize={pxToRem(14)}
                rows={5}
                placeholder={`Enter a package name, either as the name alone or in the form \\usepackage{package-name}, one per line. Multiple package names (name only) can be entered on the same line, separated by commas (without \\usepackage). Enter each user-defined command on a new line, and enter each user-defined environment on a new line as well.`}
              />
            )}
            <Flex justifyContent="flex-end">
              <Button
                colorScheme="#1D8AFF"
                onClick={handleConfirm}
                isLoading={isLoading}
                size="md"
                variant={"outline"}
                color="#1D8AFF"
                borderRadius={pxToRem(10)}
                px={6}
              >
                Confirm
              </Button>
            </Flex>
          </ModalBody>
        </ModalContent>
      </Modal>
    </Flex>
  );
};

export default LatexPanel;
